/**
 * Servicio de Traducción Multilingüe para Jean Informático C.A.
 * Idiomas soportados: Español (es), Inglés (en), Chino simplificado (zh)
 * Idioma por defecto: Español ('es')
 * Totalmente Headless y con restauración limpia al idioma nativo (Español).
 */

const STORAGE_KEY = 'app_language';
const DEFAULT_LANG = 'es';

export const IDIOMAS_DISPONIBLES = [
  {
    codigo: 'es',
    nombre: 'Español',
    nativo: 'Español',
    bandera: '🇪🇸',
    googleCode: 'es',
    predeterminado: true,
  },
  {
    codigo: 'en',
    nombre: 'Inglés',
    nativo: 'English',
    bandera: '🇺🇸',
    googleCode: 'en',
    predeterminado: false,
  },
  {
    codigo: 'zh',
    nombre: 'Chino',
    nativo: '中文 (简体)',
    bandera: '🇨🇳',
    googleCode: 'zh-CN',
    predeterminado: false,
  },
];

function setCookie(name, value, days = 365) {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  const host = window.location.hostname;
  document.cookie = `${name}=${value || ''}${expires}; path=/;`;
  if (host && host !== 'localhost' && !host.startsWith('127.')) {
    document.cookie = `${name}=${value || ''}${expires}; path=/; domain=${host};`;
    document.cookie = `${name}=${value || ''}${expires}; path=/; domain=.${host};`;
  }
}

/**
 * Elimina todas las cookies de googtrans en todos los dominios y subrutas posibles
 */
export function limpiarCookiesTraductor() {
  if (typeof window === 'undefined') return;

  const host = window.location.hostname;
  const parts = host.split('.');
  const dominios = ['', host, '.' + host];

  if (parts.length >= 2) {
    const rootDomain = parts.slice(-2).join('.');
    dominios.push(rootDomain, '.' + rootDomain);
  }

  const paths = ['/', '/app', window.location.pathname];

  dominios.forEach((d) => {
    paths.forEach((p) => {
      const dAttr = d ? `; domain=${d}` : '';
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${p}${dAttr};`;
      document.cookie = `googtrans=; Max-Age=-99999999; path=${p}${dAttr};`;
    });
  });

  // Intentar pulsar el botón de restaurar original de Google si está cargado
  try {
    const iframe = document.querySelector(
      'iframe.goog-te-banner-frame, iframe.VIpgJd-ZVi9od-ORHb-OEVmcd, iframe[id*=":container"]'
    );
    if (iframe && iframe.contentDocument) {
      const btn = iframe.contentDocument.querySelector(
        'button[id*="restore"], .goog-te-button button, [id*="original"]'
      );
      if (btn) btn.click();
    }
  } catch (_) {}
}

export function obtenerIdiomaActual() {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  const guardado = localStorage.getItem(STORAGE_KEY);
  if (guardado && IDIOMAS_DISPONIBLES.some((i) => i.codigo === guardado)) {
    return guardado;
  }
  return DEFAULT_LANG;
}

export function obtenerInfoIdioma(codigo) {
  const c = codigo || obtenerIdiomaActual();
  return IDIOMAS_DISPONIBLES.find((i) => i.codigo === c) || IDIOMAS_DISPONIBLES[0];
}

/**
 * Suprime de forma agresiva cualquier banner, barra o iframe inyectado por Google Translate,
 * asegurando que la interfaz de usuario se mantenga limpia, sin desplazar el contenido hacia abajo.
 */
function suprimirBannerGoogle() {
  if (typeof window === 'undefined') return;

  const ocultarElementosGoogle = () => {
    if (document.body) {
      if (document.body.style.top && document.body.style.top !== '0px') {
        document.body.style.setProperty('top', '0px', 'important');
      }
      if (document.body.style.position && document.body.style.position === 'relative') {
        document.body.style.setProperty('position', 'static', 'important');
      }
    }
    if (document.documentElement) {
      if (document.documentElement.style.top && document.documentElement.style.top !== '0px') {
        document.documentElement.style.setProperty('top', '0px', 'important');
      }
    }

    const selectores = [
      'iframe.goog-te-banner-frame',
      'iframe.skiptranslate',
      'iframe.VIpgJd-ZVi9od-ORHb-OEVmcd',
      '.VIpgJd-ZVi9od-ORHb-OEVmcd',
      '.VIpgJd-ZVi9od-l4eHX-hSRGPd',
      '#goog-gt-tt',
      '.goog-te-balloon-frame',
      '.goog-tooltip',
      'div.skiptranslate:not(#google_translate_element)',
      'iframe[src*="translate.google"]',
      'iframe[id*=":container"]',
      'iframe[id*=":1."]',
      'iframe[id*=":2."]'
    ];

    try {
      const elementos = document.querySelectorAll(selectores.join(','));
      elementos.forEach((el) => {
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('opacity', '0', 'important');
        el.style.setProperty('height', '0px', 'important');
        el.style.setProperty('width', '0px', 'important');
        el.style.setProperty('position', 'absolute', 'important');
        el.style.setProperty('top', '-9999px', 'important');
        el.style.setProperty('left', '-9999px', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
      });
    } catch (_) {}
  };

  if (!document.getElementById('google-translate-headless-style')) {
    const style = document.createElement('style');
    style.id = 'google-translate-headless-style';
    style.innerHTML = `
      body, html {
        top: 0px !important;
        position: static !important;
        margin-top: 0px !important;
        padding-top: 0px !important;
      }
      .goog-te-banner-frame,
      .goog-te-banner-frame.skiptranslate,
      iframe.goog-te-banner-frame,
      iframe.skiptranslate,
      iframe.VIpgJd-ZVi9od-ORHb-OEVmcd,
      div.VIpgJd-ZVi9od-ORHb-OEVmcd,
      .VIpgJd-ZVi9od-ORHb-OEVmcd,
      .VIpgJd-ZVi9od-l4eHX-hSRGPd,
      #goog-gt-tt,
      .goog-te-balloon-frame,
      .goog-tooltip,
      body > iframe[src*="translate.google"],
      body > iframe[id*=":container"],
      body > iframe[id*=":1."],
      body > iframe[id*=":2."],
      body > .skiptranslate:not(#google_translate_element) {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        height: 0px !important;
        width: 0px !important;
        position: absolute !important;
        top: -9999px !important;
        left: -9999px !important;
        pointer-events: none !important;
        border: none !important;
      }
      font {
        background-color: transparent !important;
        box-shadow: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  ocultarElementosGoogle();

  const observer = new MutationObserver(() => {
    ocultarElementosGoogle();
  });

  const iniciarObserver = () => {
    if (document.body) {
      observer.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'], childList: true });
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciarObserver);
  } else {
    iniciarObserver();
  }

  const interval = setInterval(ocultarElementosGoogle, 200);
  setTimeout(() => clearInterval(interval), 6000);
}

export function inicializarTraductor() {
  if (typeof window === 'undefined') return;

  const idiomaActual = obtenerIdiomaActual();

  // Si el idioma es español (el idioma nativo de toda la plataforma):
  // Eliminamos cualquier cookie de traducción activa y NO cargamos el script de Google.
  if (idiomaActual === 'es') {
    limpiarCookiesTraductor();
    return;
  }

  // Si el idioma es inglés o chino, activamos la supresión y cargamos el traductor
  suprimirBannerGoogle();

  const info = obtenerInfoIdioma(idiomaActual);
  setCookie('googtrans', `/es/${info.googleCode}`, 365);

  // Inyectar contenedor headless
  if (!document.getElementById('google_translate_element')) {
    const div = document.createElement('div');
    div.id = 'google_translate_element';
    div.style.cssText = 'display:none !important; position:absolute; left:-9999px; top:-9999px; width:0; height:0;';
    div.className = 'notranslate';
    div.setAttribute('translate', 'no');
    document.body.appendChild(div);
  }

  // Callback de Google Translate
  window.googleTranslateElementInit = function () {
    try {
      if (window.google && window.google.translate) {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'es',
            includedLanguages: 'en,zh-CN',
            autoDisplay: false,
          },
          'google_translate_element'
        );
        suprimirBannerGoogle();
      }
    } catch (e) {
      console.warn('⚠️ Error al inicializar TranslateElement:', e);
    }
  };

  // Inyectar script de Google Translate
  if (!document.getElementById('google-translate-script')) {
    const script = document.createElement('script');
    script.id = 'google-translate-script';
    script.type = 'text/javascript';
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.head.appendChild(script);
  }
}

export function cambiarIdioma(codigo) {
  const langObj = IDIOMAS_DISPONIBLES.find((i) => i.codigo === codigo) || IDIOMAS_DISPONIBLES[0];
  const lang = langObj.codigo;
  const anterior = obtenerIdiomaActual();

  localStorage.setItem(STORAGE_KEY, lang);

  window.dispatchEvent(
    new CustomEvent('idiomaCambiado', {
      detail: { lang, anterior, info: langObj },
    })
  );

  if (lang === 'es') {
    // Al volver a Español:
    // Limpiamos de raíz todas las cookies de Google Translate y recargamos limpiamente.
    // Como inicializarTraductor no carga Google cuando idioma es 'es', la app queda
    // 100% en español original sin ninguna etiqueta externa.
    limpiarCookiesTraductor();
    setTimeout(() => {
      window.location.reload();
    }, 50);
    return;
  }

  // Si cambiamos a Inglés o Chino:
  setCookie('googtrans', `/es/${langObj.googleCode}`, 365);
  suprimirBannerGoogle();

  const combo = document.querySelector('.goog-te-combo');
  if (combo) {
    combo.value = langObj.googleCode;
    combo.dispatchEvent(new Event('change'));
    suprimirBannerGoogle();
  } else {
    setTimeout(() => {
      window.location.reload();
    }, 80);
  }
}

export default {
  IDIOMAS_DISPONIBLES,
  obtenerIdiomaActual,
  obtenerInfoIdioma,
  inicializarTraductor,
  cambiarIdioma,
  limpiarCookiesTraductor,
};
